import { useState, useEffect } from 'react';
import { X, Sparkles, Gift, Zap, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'winwgo_welcome_dismissed';

interface WelcomeBannerProps {
  onShowHowItWorks?: () => void;
}

export function WelcomeBanner({ onShowHowItWorks }: WelcomeBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if banner was already dismissed
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      // Slight delay for entrance animation
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem(STORAGE_KEY, 'true');
    }, 200);
  };

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-5 sm:p-6 mb-6 sm:mb-8',
        'transition-all duration-300 ease-out',
        isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      )}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />

      {/* Close button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Dismiss welcome banner"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/20">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground">
            Welcome to WinWGo!
          </h2>
        </div>

        <p className="text-sm sm:text-base text-muted-foreground mb-5 max-w-lg leading-relaxed">
          Get AI-powered football predictions and betting tips. Browse matches, unlock expert analysis, and make smarter betting decisions.
        </p>

        {/* Quick benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div className="flex items-center gap-2.5 text-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success/10">
              <TrendingUp className="h-4 w-4 text-success" />
            </div>
            <span className="font-medium">Expert predictions</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
              <Gift className="h-4 w-4 text-amber-500" />
            </div>
            <span className="font-medium">Free daily credits</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-live/10">
              <Zap className="h-4 w-4 text-live" />
            </div>
            <span className="font-medium">Live match analysis</span>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={onShowHowItWorks}
            className="h-11 px-5 font-semibold shadow-sm"
          >
            See How It Works
          </Button>
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="h-11 px-5 font-medium"
          >
            Got it, let's go!
          </Button>
        </div>
      </div>
    </div>
  );
}
